/* ---------------- Status / template lookup ---------------- */

/**
 * Numeric campaign status codes returned by the backend. Map mirrors the
 * legacy STATUS_MAP. Two distinct numbers can share a label (e.g. 4 and 5
 * both render as "Error").
 */
export const STATUS_MAP: Record<string, number | number[]> = {
  Ready: 0,
  Processing: 21,
  Running: 1,
  Complete: 2,
  'Low Balance': 3,
  Error: [4, 5],
  Cancelling: 6,
  Cancelled: 7,
  Pausing: 8,
  Paused: 9,
  Resuming: 10,
  Timeout_Pausing: 22,
  Timeout_Paused: 23,
  'System Cancelled': 12,
  'System Complete': 11,
};

const buildCodeToLabel = (): Record<number, string> => {
  const out: Record<number, string> = {};
  for (const [label, code] of Object.entries(STATUS_MAP)) {
    if (Array.isArray(code)) code.forEach((c) => (out[c] = label));
    else out[code] = label;
  }
  return out;
};
export const STATUS_CODE_TO_LABEL = buildCodeToLabel();

/** Sort priority for the status column (mirrors legacy STATUS_PRIORITY). */
export const STATUS_PRIORITY: Record<string, number> = {
  Processing: 0,
  Running: 1,
  Complete: 2,
  Paused: 3,
  Timeout_Paused: 4,
  Ready: 5,
  'Low Balance': 6,
  Error: 7,
  Cancelling: 8,
  Cancelled: 9,
  Pausing: 10,
  Resuming: 11,
  Timeout_Pausing: 12,
  'System Cancelled': 13,
  'System Complete': 14,
};

export const TEMPLATES: Record<number, string> = {
  0: 'Simple IVR',
  1: 'DTMF',
  2: 'Call Patch',
  3: 'Custom IVR',
  4: 'Multi IVR',
  6: 'Inbound Call Patch',
  7: 'TTS Simple IVR',
  8: 'TTS DTMF',
  9: 'TTS Call Patch',
};

/** Numeric status → human label, '' when unknown. */
export function statusLabel(code: number | string | undefined | null): string {
  if (code === null || code === undefined || code === '') return '';
  const n = typeof code === 'string' ? Number(code) : code;
  return STATUS_CODE_TO_LABEL[n] ?? '';
}

export function templateLabel(id: number | string | undefined | null): string {
  if (id === null || id === undefined || id === '') return '';
  const n = typeof id === 'string' ? Number(id) : id;
  return TEMPLATES[n] ?? '';
}

/* ---------------- Campaign row shape ---------------- */

/**
 * The shape of a campaign as returned by any of the list endpoints. Optional
 * fields reflect that admin views add owner info and that some sub-feature
 * counters (TTS, webhook, retry) are absent from older or simpler campaigns.
 */
export interface CampaignRow {
  campaignId: number;
  campaignName: string;
  /** Numeric status code — see STATUS_MAP for the label mapping. */
  campaignStatus: number;
  templateId: number;

  /** Schedule + lifecycle timestamps (server formats these as strings). */
  scheduleTime?: string;
  startTime?: string;
  startDate?: string;
  endTime?: string;
  flowEndTime?: string;
  pauseTime?: string;

  /* Counters. */
  numbersUploaded?: number;
  numbersProcessed?: number;
  callsConnected?: number;
  pulses?: number;
  dndCount?: number;
  dtmfCount?: number;
  dtmf1Count?: number;
  dtmf2Count?: number;
  smsCount?: number;
  ttsCount?: number;
  retries?: number;
  retryCount?: number;
  failedCount?: number;
  isRetry?: number;
  channels?: number;
  /** 1 = continuous campaign (no fixed base file); 0 = standard. */
  continuous?: number;

  /* Report-readiness flags ("2" = ready). */
  fullReportStatus?: string;
  dtmfReportStatus?: string;
  ccReportStatus?: string;
  smsStatus?: string | number;

  /* Webhook counters / flags. */
  hasWebhook?: string | number;
  webhookLog?: string | number;
  webhookSuccessCount?: number;
  webhookFailedCount?: number;
  retryWebhookCount?: number;

  /* Misc routing info. */
  ttsFlowId?: number | string;
  locationList?: string;
  cliList?: string;

  /* Admin/owner fields — only present on admin/superadmin endpoints. */
  userId?: number;
  username?: string;
  /** Legacy admin views also expose owner display name separately. */
  name?: string;

  /** Reseller-tier metadata in some admin views. */
  resellerUsername?: string;
  /** SADMIN-only: username of the user's parent (the reseller). */
  parentUsername?: string;
  parentId?: number;
  sParentId?: number;
}

/* ---------------- Filter / action requests ---------------- */

export interface HistoricalCampaignRequest {
  userId: number;
  startDate: string;
  endDate: string;
}

export interface AdminHistoricalCampaignRequest extends HistoricalCampaignRequest {
  /** Filter by owner username (optional). */
  username?: string;
}

export interface CampaignActionRequest {
  /** Most lifecycle endpoints (stop / pause / resume) take just this. */
  campaignId: number;
  /**
   * Required by `/campaign/cancel` — must be 7 (cancel) or 2 (force-fail).
   * Other endpoints ignore the field, so it's safe to always omit.
   */
  status?: number;
}

/**
 * Per-row inline-download kinds — drives the ⬇ icon next to count cells in
 * the campaign list. Lives here (not in columns.tsx) so api.ts can reference
 * it without creating an `api ⇄ columns` import cycle.
 */
export type ReportDownloadKind =
  | 'dtmf'
  | 'dtmf1'
  | 'dtmf2'
  | 'connected_calls'
  | 'sms'
  | 'pending';

/* ---------------- Derived display helpers ---------------- */

/** Pending calls = total uploaded - DND - already processed. */
export function pendingCalls(row: CampaignRow): number {
  return (
    (row.numbersUploaded ?? 0) -
    (row.dndCount ?? 0) -
    (row.numbersProcessed ?? 0)
  );
}

/** Total calls dialed = numbersProcessed + retryCount (legacy formula). */
export function totalCallsDialed(row: CampaignRow): number {
  return (row.numbersProcessed ?? 0) + (row.retryCount ?? 0);
}
